import { useState, useEffect, useCallback } from "react";
import "./App.css";
import Modal from "./components/Modal";
import axios from "./axios";
import AdCard from "./components/AdCard";
import AdCardSkeleton from "./components/AdCardSkeleton";
import { Routes, Route, useParams, useLocation } from "react-router-dom";
import AdDetails from "./components/AdDetails";
import { useNavigate } from "react-router-dom";
import MainLayout from "./components/MainLayout";
import PasswordResetModal from "./components/PasswordResetModal";
import MyAds from "./components/MyAds";
import CreateAdPage from "./components/CreateAdPage";
import EditAdPage from "./components/EditAdPage";
import React from "react";
import CategoryPickerPage from "./components/CategoryPickerPage";
import ChatPage from "./components/chat/ChatPage";
import ProfilePage from "./components/ProfilePage";
import FavoritesPage from "./components/FavoritesPage";
import NotificationsPage from "./components/NotificationsPage";
import PublicProfilePage from "./components/PublicProfilePage";
import SimilarAdsPage from "./components/SimilarAdsPage";


function App() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [popularSubcategories, setPopularSubcategories] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [onlyWithImage, setOnlyWithImage] = useState(false);
  const [location, setLocation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authNotice, setAuthNotice] = useState("");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const routerLocation = useLocation();

  const resetFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setLocation("");
    setOnlyWithImage(false);
    setSelectedCategory("");
    setSearchTerm("");

    if (window.location.pathname.startsWith("/category/")) {
      navigate("/"); // сброс категории = переход на главную
    } else {
      fetchAds(); // на главной просто обновляем список
    }
  };

  const handlePickCategory = (id) => {
    setSelectedCategory(String(id));
    navigate(`/category/${id}`);
  };


const fetchAds = useCallback(async () => {
  setLoading(true); // Показываем скелеты

  try {
    const response = await axios.get("/api/ads/", {
      params: {
        search: searchTerm,
        price__gte: minPrice || undefined,
        price__lte: maxPrice || undefined,
        location__icontains: location || undefined,
        images__isnull: onlyWithImage ? 'false' : undefined,
        category: selectedCategory || undefined,
      },
    });
    setAds(response.data);
  } catch (error) {
    console.error("Σφάλμα φόρτωσης αγγελιών:", error);
  } finally {
    setLoading(false); // Скрываем скелеты
  }
}, [searchTerm, minPrice, maxPrice, onlyWithImage, location, selectedCategory]);

  const handleLogout = () => {
    setUser(null);
    setToken("");
    localStorage.removeItem("token");
  };

  const handleProfileClick = () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    navigate("/profile");
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.error("Δεν επιλέχθηκε αρχείο!");
      return;
    }
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const token = localStorage.getItem("token");

      // 1️⃣ Загружаем аватар на бэкенд
      await axios.post("/api/update-avatar/", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2️⃣ Сразу же обновляем объект пользователя из /api/user/
      const meRes = await axios.get("/api/user/", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUser(meRes.data);          // тут уже полный avatar-URL из сериализатора
    } catch (error) {
      console.error("Σφάλμα κατά τη μεταφόρτωση φωτογραφίας:", error);
    }
  };

  useEffect(() => {
    const openReset = () => setShowPasswordResetModal(true);
    window.addEventListener("open-password-reset", openReset);

    return () => {
      window.removeEventListener("open-password-reset", openReset);
    };
  }, []);

  useEffect(() => {
    let openTimer;
    let hideTimer;

    const openLogin = (event) => {
      const text =
        event?.detail?.message || "Παρακαλώ συνδεθείτε για να συνεχίσετε.";

      setAuthNotice(text);

      hideTimer = setTimeout(() => {
        setAuthNotice("");
      }, 2200);

      openTimer = setTimeout(() => {
        setShowLoginModal(true);
      }, 900);
    };

    window.addEventListener("open-login-modal", openLogin);

    return () => {
      window.removeEventListener("open-login-modal", openLogin);
      clearTimeout(openTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    if (!token) return;

    axios.get("/api/user/", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setUser(res.data);
      })
      .catch((error) => {
        console.warn("⛔️ Δεν ήταν δυνατή η φόρτωση χρήστη:", error.response?.status);
        // ❌ НИЧЕГО НЕ СБРАСЫВАЙ ПРИ ОШИБКЕ
      });
  }, [token]);


  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

useEffect(() => {
  axios.get("/api/categories/")
    .then((res) => {
      setCategories(res.data);
      const subcategories = res.data
        .flatMap((category) => category.subcategories || [])
        .filter(Boolean)
        .slice(0, 10);
      setPopularSubcategories(subcategories);
    })
    .catch((error) => console.error("Σφάλμα φόρτωσης κατηγοριών:", error));
}, []);

function CategoryBootstrap({ onInit, children }) {
  const { id } = useParams();
  useEffect(() => {
    if (id) onInit(String(id));   // кладём :id из URL в selectedCategory
  }, [id, onInit]);
  return children;
}


useEffect(() => {
  if (routerLocation.pathname === "/") {
    // сброс только категории (остальные фильтры оставляем как есть)
    setSelectedCategory("");
  }
}, [routerLocation.pathname]);


// находит категорию по id во всём дереве
function findCategoryById(list, id) {
  for (const c of list) {
    if (String(c.id) === String(id)) return c;
    if (c.subcategories?.length) {
      const hit = findCategoryById(c.subcategories, id);
      if (hit) return hit;
    }
  }
  return null;
}

function renderCategoryOptionsTree(list) {
  const NBSP = "\u00A0";
  const IND2 = NBSP.repeat(2);
  const IND3 = NBSP.repeat(6);

  return list
    .filter(c => !c.parent) // только корневые
    .flatMap(cat => {
      const groupHeader = (
        <option key={`hdr-${cat.id}`} value="" disabled>
          {cat.name} ›
        </option>
      );

      // 🔹 Вот тут добавляем опцию "Все объявления" для этого раздела
      const allAdsOption = (
        <option key={`all-${cat.id}`} value={cat.id}>
          — Όλες οι αγγελίες ({cat.name})
        </option>
      );

      const level2 = (cat.subcategories || []).flatMap(sub => {
        const opt2 = (
          <option key={`s-${sub.id}`} value={sub.id}>
            {IND2}• {sub.name}
          </option>
        );

        const level3 = (sub.subcategories || []).map(sub2 => (
          <option key={`ss-${sub2.id}`} value={sub2.id}>
            {IND3} {sub2.name}
          </option>
        ));

        return [opt2, ...level3];
      });

      const separator = (
        <option key={`sep-${cat.id}`} value="" disabled>
          ─────────────────
        </option>
      );

      return [groupHeader, allAdsOption, ...level2, separator];
    });
}

// строит путь от корня до категории (для хлебных крошек)
function buildCategoryPath(list, id) {
  function dfs(node, targetId, trail) {
    const nextTrail = [...trail, node];
    if (String(node.id) === String(targetId)) return nextTrail;
    if (node.subcategories?.length) {
      for (const child of node.subcategories) {
        const res = dfs(child, targetId, nextTrail);
        if (res) return res;
      }
    }
    return null;
  }

  for (const root of list) {
    const res = dfs(root, id, []);
    if (res) return res;
  }
  return [];
}

return (
  <>
    <Routes>
      <Route
        path="/"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <div className="main-content">
              <div className="filters-sidebar">
                <h3>Φίλτρα</h3>
                <input
                  type="number"
                  placeholder="Τιμή από"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="search-input"
                />
                <input
                  type="number"
                  placeholder="Τιμή έως"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="search-input"
                />
                <input
                  type="text"
                  placeholder="Πόλη / Περιοχή"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="search-input"
                />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="search-input"
                >
                  <option value="">Όλες οι κατηγορίες</option>
                  {renderCategoryOptionsTree(categories)}
                </select>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={onlyWithImage}
                    onChange={(e) => setOnlyWithImage(e.target.checked)}
                  />
                  Μόνο με φωτογραφία
                </label>
                <button onClick={fetchAds} className="search-button">
                  Εφαρμογή φίλτρων
                </button>
                <button onClick={resetFilters} className="search-button reset">
                  Εκκαθάριση φίλτρων
                </button>
              </div>

              <div className="ads-list">
                <h2>Αποτελέσματα:</h2>

                {loading ? (
                  <div className="ads-grid">
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <AdCardSkeleton key={n} />
                    ))}
                  </div>
                ) : ads.length === 0 ? (
                  <div style={{ padding: 24, opacity: 0.7 }}>
                    Δεν βρέθηκαν αγγελίες για τα επιλεγμένα φίλτρα.
                  </div>
                ) : (
                  <div className="ads-grid">
                    {ads.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </MainLayout>
        }
      />
      <Route
        path="/category/:id"
        element={
          <CategoryBootstrap onInit={(id) => setSelectedCategory(id)}>
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
          {selectedCategory && (
            <div style={{ margin: "8px 0 16px" }}>
              {/* хлебные крошки */}
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {buildCategoryPath(categories, selectedCategory).map((c, idx, arr) => {
                  const isLast = idx === arr.length - 1;
                  return (
                    <span key={c.id}>
                      {!isLast ? (
                        <button
                          type="button"
                          onClick={() => handlePickCategory(c.id)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            color: "#1a73e8",
                            textDecoration: "underline",
                            font: "inherit",
                          }}
                        >
                          {c.name}
                        </button>
                      ) : (
                        <strong>{c.name}</strong>
                      )}
                      {!isLast && " / "}
                    </span>
                  );
                })}
              </div>

              {/* крупный заголовок текущей категории */}
              <h1 style={{ margin: "6px 0 0", fontSize: 22, fontWeight: 700 }}>
                {findCategoryById(categories, selectedCategory)?.name || "Κατηγορία"}
              </h1>
            </div>
          )}
            <div className="main-content">
              <div className="filters-sidebar">
                <h3>Φίλτρα</h3>
                <input
                  type="number"
                  placeholder="Τιμή από"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="search-input"
                />
                <input
                  type="number"
                  placeholder="Τιμή έως"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="search-input"
                />
                <input
                  type="text"
                  placeholder="Πόλη / Περιοχή"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="search-input"
                />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="search-input"
                >
                  <option value="">Όλες οι κατηγορίες</option>
                  {renderCategoryOptionsTree(categories)}
                </select>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={onlyWithImage}
                    onChange={(e) => setOnlyWithImage(e.target.checked)}
                  />
                  Μόνο με φωτογραφία
                </label>
                <button onClick={fetchAds} className="search-button">
                  Εφαρμογή φίλτρων
                </button>
                <button onClick={resetFilters} className="search-button reset">
                  Εκκαθάριση φίλτρων
                </button>
              </div>

              <div className="ads-list">
                <h2>Αποτελέσματα:</h2>

                {loading ? (
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <AdCardSkeleton key={n} />
                    ))}
                  </div>
                ) : ads.length === 0 ? (
                  <div style={{ padding: 24, opacity: 0.7 }}>
                    Δεν βρέθηκαν αγγελίες για τα επιλεγμένα φίλτρα.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {ads.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </MainLayout>
        </CategoryBootstrap>
      }
    />
      <Route
        path="/ads/:id"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <AdDetails user={user} />
          </MainLayout>
        }
      />
      <Route
        path="/ads/:id/edit"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <EditAdPage />
          </MainLayout>
        }
      />
      <Route
        path="/chat"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <ChatPage
              token={token}
              currentProfileId={user?.profile?.id}
            />
          </MainLayout>
        }
      />
      <Route
        path="/my-ads"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <MyAds />
          </MainLayout>
        }
      />
      <Route
        path="/similar/:id"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <SimilarAdsPage />
          </MainLayout>
        }
      />
      <Route
        path="/favorites"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <FavoritesPage />
          </MainLayout>
        }
      />
      <Route
        path="/notifications"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <NotificationsPage onRequireAuth={() => setShowLoginModal(true)} />
          </MainLayout>
        }
      />
      <Route
        path="/profile"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <ProfilePage user={user} setUser={setUser} token={token} />
          </MainLayout>
        }
      />
      <Route
        path="/seller/:profileId"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <PublicProfilePage />
          </MainLayout>
        }
      />
      {/* 1) ПЕРВЫЙ ЭКРАН: выбор категории */}
      <Route
        path="/create"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <CategoryPickerPage categories={categories} />
          </MainLayout>
        }
      />

      {/* 2) ФОРМА: после выбора категории */}
      <Route
        path="/create/:categoryId"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
            <CreateAdPage />
          </MainLayout>
        }
      />
      <Route
        path="/create/:categoryId"
        element={
          <MainLayout
            user={user}
            setUser={setUser}
            token={token}
            setToken={setToken}
            showLoginModal={showLoginModal}
            setShowLoginModal={setShowLoginModal}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            handleAvatarChange={handleAvatarChange}
            handleLogout={handleLogout}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            fetchAds={fetchAds}
            popularSubcategories={popularSubcategories}
            onPickCategory={handlePickCategory}
            onProfileClick={handleProfileClick}
          >
      <CreateAdPage />
    </MainLayout>
  }
/>
    </Routes>

    {authNotice && (
      <div className="auth-notice">
        {authNotice}
      </div>
    )}
    <Modal isOpen={showPasswordResetModal} onClose={() => setShowPasswordResetModal(false)}>
      <PasswordResetModal onClose={() => setShowPasswordResetModal(false)} />
    </Modal>
  </>
);
}

export default App;
